import React, { useState, useRef, useEffect } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { eclipse } from "@uiw/codemirror-theme-eclipse";
import OpenAI from "openai";
import Groq from "groq-sdk";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { addressService } from "../../store/actions";
import { get0xAddress } from "../../common/utils/function";

const MODEL_OPTIONS: Record<string, { label: string; value: string }[]> = {
  openai: [
    { label: "gpt-3.5-turbo", value: "gpt-3.5-turbo" },
    { label: "gpt-4o", value: "gpt-4o" },
  ],
  groq: [{ label: "grok-4-latest", value: "grok-4-latest" }],
  gemini: [
    { label: "gemini-flash-latest", value: "gemini-flash-latest" },
    { label: "gemini-1.5-pro-latest", value: "gemini-1.5-pro-latest" },
  ],
};

interface Message {
  role: "user" | "assistant" | "system";
  content: string;
}

const AIPage = () => {
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-3.5-turbo");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saveKey, setSaveKey] = useState(false);
  const [address, setAddress] = useState("");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingContract, setLoadingContract] = useState(false);
  const [sourceCode, setSourceCode] = useState("");
  const [contractName, setContractName] = useState("");
  const [contractVerified, setContractVerified] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const savedKey = localStorage.getItem(`api_key_${provider}`);
    if (savedKey) {
      setApiKey(savedKey);
      setSaveKey(true);
    }
  }, [provider]);

  useEffect(() => {
    setModel(MODEL_OPTIONS[provider][0].value);
  }, [provider]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider);
  };

  const handleLoad = async () => {
    if (!address.trim()) {
      alert("Please enter a contract address");
      return;
    }

    setLoadingContract(true);
    try {
      const formattedAddress = get0xAddress(address);
      const response = await addressService.getVerifiedAddress(
        formattedAddress,
        ""
      );
      if (response.is_verified) {
        setSourceCode(response.source_code || "");
        setContractName(response.file_path || "");
        setContractVerified(true);
      } else {
        alert("Contract is not verified");
        setContractVerified(false);
        setSourceCode("");
      }
    } catch (error: any) {
      alert(error.message || "Failed to load contract");
      setContractVerified(false);
    } finally {
      setLoadingContract(false);
    }
  };

  const sendMessage = async () => {
    if (!prompt.trim()) return;
    if (!apiKey.trim()) {
      alert("Please enter your API key");
      return;
    }

    const userMessage: Message = { role: "user", content: prompt };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt("");
    setLoading(true);

    try {
      let assistantContent = "";
      const systemMessage = contractVerified
        ? {
            role: "system" as const,
            content: `You are helping with this smart contract: ${contractName}\n\nSource code:\n\`\`\`solidity\n${sourceCode}\n\`\`\``,
          }
        : {
            role: "system" as const,
            content: "You are a helpful AI assistant.",
          };

      if (provider === "openai") {
        const client = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
        const response = await client.chat.completions.create({
          model,
          messages: [systemMessage, userMessage],
        });
        assistantContent =
          response.choices[0]?.message?.content || "No response";
      } else if (provider === "groq") {
        const client = new Groq({ apiKey });
        const response = await client.chat.completions.create({
          model,
          messages: [systemMessage, userMessage],
        });
        assistantContent =
          response.choices[0]?.message?.content || "No response";
      } else if (provider === "gemini") {
        const genAI = new GoogleGenerativeAI(apiKey);
        const modelInstance = genAI.getGenerativeModel({ model });
        const fullPrompt = `${systemMessage.content}\n\nUser: ${prompt}`;
        const response = await modelInstance.generateContent(fullPrompt);
        assistantContent = response.response.text() || "No response";
      }

      const assistantMessage: Message = {
        role: "assistant",
        content: assistantContent,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      if (saveKey) {
        localStorage.setItem(`api_key_${provider}`, apiKey);
      }
    } catch (error: any) {
      const errorMessage: Message = {
        role: "assistant",
        content:
          error.message || "Failed to get response. Please check your API key.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example: string) => {
    if (!contractVerified) {
      alert("Please load a verified contract first");
      return;
    }
    setPrompt(example);
  };

  return (
    <div className="container py-3">
      <div className="row g-3">
        <div className="col-12 col-lg-5 col-xl-4">
          <div className="bs-card p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <div className="page-title">Code Reader</div>
                <div className="text-muted small">
                  Generate instant answers for your smart contract questions
                  using AI Models.
                </div>
              </div>
              <span className="badge bg-secondary-subtle text-secondary border">
                Beta
              </span>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium small text-uppercase text-muted">
                Choose Provider
              </label>
              <select
                className="form-select form-select-sm"
                value={provider}
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                <option value="openai">OpenAI</option>
                <option value="groq">Groq</option>
                <option value="gemini">Gemini</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium small text-uppercase text-muted">
                Choose Model
              </label>
              <select
                className="form-select form-select-sm"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {MODEL_OPTIONS[provider].map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-2 d-flex align-items-center gap-2">
              <div className="fw-medium small text-uppercase text-muted">
                API Key
              </div>
            </div>
            <div className="mb-2 input-group input-group-sm">
              <input
                type={showKey ? "text" : "password"}
                className="form-control"
                placeholder="Enter API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowKey(!showKey)}
              >
                <i className={`bi bi-${showKey ? "eye-slash" : "eye"}`}></i>
              </button>
            </div>
            <div className="form-check mb-3">
              <input
                id="saveKey"
                className="form-check-input"
                type="checkbox"
                checked={saveKey}
                onChange={(e) => setSaveKey(e.target.checked)}
              />
              <label
                htmlFor="saveKey"
                className="form-check-label small text-muted"
              >
                Save API Key
              </label>
            </div>

            <div className="mb-2 fw-medium small">Examples</div>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <button
                className="btn-primary  btn btn-sm"
                type="button"
                onClick={() =>
                  handleExampleClick("What does this contract do?")
                }
              >
                What does this contract do?
              </button>
              <button
                className="btn-primary  btn btn-sm"
                type="button"
                onClick={() =>
                  handleExampleClick(
                    "What are the functions available in this contract?"
                  )
                }
              >
                What are the functions?
              </button>
            </div>

            <div className="mb-2 fw-medium small">Contract Address</div>
            <div className="input-group input-group-sm">
              <input
                className="form-control"
                placeholder="0x.."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <button
                className="btn btn-dark px-3"
                type="button"
                onClick={handleLoad}
                disabled={loadingContract}
              >
                {loadingContract ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  "Load"
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-7 col-xl-8">
          <div className="bs-card p-3 h-100" style={{ minHeight: "600px" }}>
            {contractVerified && sourceCode ? (
              <>
                <div className="mb-2 fw-medium small text-uppercase text-muted">
                  Source Code
                </div>
                <div style={{ height: "400px", overflow: "auto" }}>
                  <CodeMirror
                    value={sourceCode}
                    height="100%"
                    theme={eclipse}
                    extensions={[javascript({ jsx: true })]}
                    editable={false}
                  />
                </div>
                <div className="border-top mt-3 pt-3">
                  <div className="mb-2 fw-medium small text-uppercase text-muted">
                    Chat
                  </div>
                  <div
                    className="border rounded p-2 mb-2"
                    style={{ height: "150px", overflow: "auto" }}
                  >
                    {messages.length === 0 && (
                      <div className="text-muted small text-center">
                        Start chatting about the contract...
                      </div>
                    )}
                    {messages.map((msg, idx) => (
                      <div key={idx} className="mb-2">
                        <div className="fw-medium small text-muted">
                          {msg.role === "user" ? "You" : "AI"}
                        </div>
                        <div className="small">{msg.content}</div>
                      </div>
                    ))}
                    {loading && (
                      <div className="text-muted small">AI is thinking...</div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="input-group input-group-sm">
                    <input
                      className="form-control"
                      placeholder="Ask about the contract..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                      disabled={loading}
                    />
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={sendMessage}
                      disabled={loading}
                    >
                      <i className="bi bi-send"></i>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                <div className="text-center">
                  <i className="bi bi-file-earmark-code fs-1 mb-3"></i>
                  <div>Load a verified contract to view source code</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPage;
